import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../lib/apiBase";
import { getAuthHeader } from "../lib/auth";

async function request(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    method: options.method || "GET",
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      Authorization: getAuthHeader(),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    cache: "no-store",
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.message || data?.error || `Request failed (${response.status})`);
  return data;
}

function statusDate(value) {
  if (!value) return "Not yet";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not yet" : date.toLocaleString();
}

export default function ConsoleStreaming() {
  const [profile, setProfile] = useState(null);
  const [crew, setCrew] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({ twitchChannel: "", platform: "ps5" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const [mine, founder] = await Promise.allSettled([
      request("/console-streaming/me"),
      request("/console-streaming/admin"),
    ]);
    if (mine.status === "fulfilled") {
      setProfile(mine.value?.profile || null);
      setForm({
        twitchChannel: mine.value?.profile?.twitchChannel || "",
        platform: mine.value?.profile?.platform || "ps5",
      });
    }
    if (founder.status === "fulfilled") setCrew(founder.value?.crew || []);
    else setCrew(null);
    if (mine.status === "rejected") setError(mine.reason?.message || "Console streaming access is not enabled for this account.");
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function saveSource(event) {
    event.preventDefault();
    setBusy("save");
    setError("");
    setMessage("");
    try {
      const result = await request("/console-streaming/me/source", {
        method: "PATCH",
        body: form,
      });
      setProfile(result.profile);
      setMessage(`Console source connected. ${result.profile.channelName} is ready to use ${result.profile.platform === "xbox" ? "Xbox" : "PS5"} Direct.`);
    } catch (err) {
      setError(err.message || "Could not save the console source.");
    } finally {
      setBusy("");
    }
  }

  async function clearSource() {
    setBusy("clear");
    setError("");
    setMessage("");
    try {
      const result = await request("/console-streaming/me/source", { method: "DELETE" });
      setProfile(result.profile);
      setForm((current) => ({ ...current, twitchChannel: "" }));
      setMessage("Direct console source disconnected. Your GA channel and OBS setup were kept.");
    } catch (err) {
      setError(err.message || "Could not disconnect the direct console source.");
    } finally {
      setBusy("");
    }
  }

  async function toggleWebsiteMusic() {
    if (!profile) return;
    setBusy("music");
    setError("");
    setMessage("");
    try {
      const nextEnabled = !profile.websiteMusicEnabled;
      const result = await request("/console-streaming/me/music", {
        method: "PATCH",
        body: { enabled: nextEnabled },
      });
      setProfile(result.profile);
      setMessage(
        nextEnabled
          ? "Website music is ON for your console-direct channel."
          : "Website music is OFF. Your OBS/Mux stream setup was not changed."
      );
    } catch (err) {
      setError(err.message || "Could not update website music.");
    } finally {
      setBusy("");
    }
  }

  async function reconcile() {
    setBusy("reconcile");
    setError("");
    setMessage("");
    try {
      const result = await request("/console-streaming/admin/reconcile", { method: "POST" });
      setCrew(result.crew || []);
      setMessage("Crew console access, channels, and pending onboarding notifications were reconciled.");
      await load();
    } catch (err) {
      setError(err.message || "Could not reconcile the console crew.");
    } finally {
      setBusy("");
    }
  }

  async function adminAction(entry, action) {
    setBusy(`${action}:${entry.id}`);
    setError("");
    setMessage("");
    try {
      const result = action === "mux"
        ? await request(`/console-streaming/admin/${entry.id}/mux`, { method: "POST" })
        : await request(`/console-streaming/admin/${entry.id}`, {
            method: "PATCH",
            body: { enabled: !entry.enabled },
          });
      setCrew(result.crew || []);
      setMessage(action === "mux" ? `${entry.channelName} Mux setup retried.` : `${entry.channelName} console access ${entry.enabled ? "paused" : "enabled"}.`);
    } catch (err) {
      setError(err.message || "Could not update that streamer.");
    } finally {
      setBusy("");
    }
  }

  if (loading) return <main className="gacs-page"><div className="gacs-card">Loading console streaming…</div></main>;

  return (
    <main className="gacs-page">
      <style>{`
        .gacs-page{width:min(1180px,calc(100% - 24px));margin:0 auto 80px;color:#fff}.gacs-page *{box-sizing:border-box}.gacs-hero,.gacs-card{border:1px solid rgba(255,255,255,.12);border-radius:22px;background:linear-gradient(145deg,rgba(42,7,13,.95),rgba(6,7,12,.97));box-shadow:0 24px 70px rgba(0,0,0,.38)}.gacs-hero{padding:clamp(22px,5vw,44px);background:radial-gradient(circle at 86% 8%,rgba(213,25,42,.3),transparent 34%),linear-gradient(145deg,rgba(46,7,12,.97),rgba(5,6,10,.98))}.gacs-kicker{font-size:11px;font-weight:1000;letter-spacing:.16em;color:#ff9da5}.gacs-hero h1{font-size:clamp(2.3rem,7vw,5rem);line-height:.95;margin:8px 0 12px}.gacs-hero p,.gacs-card p{color:rgba(255,255,255,.7);line-height:1.65}.gacs-flow{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-top:18px}.gacs-flow span{padding:8px 11px;border-radius:999px;background:rgba(255,255,255,.07);font-size:11px;font-weight:900}.gacs-flow b{color:#ff5967}.gacs-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:16px}.gacs-card{padding:20px;margin-top:16px}.gacs-card h2{margin:6px 0 10px;font-size:clamp(1.5rem,4vw,2.35rem)}.gacs-form{display:grid;gap:11px}.gacs-label{display:grid;gap:6px;font-size:12px;font-weight:900;color:rgba(255,255,255,.72)}.gacs-input{width:100%;padding:12px 13px;border-radius:11px;border:1px solid rgba(255,255,255,.16);background:#090a0e;color:#fff;outline:none}.gacs-actions{display:flex;flex-wrap:wrap;gap:8px}.gacs-btn{display:inline-flex;align-items:center;justify-content:center;border:1px solid rgba(255,72,85,.42);border-radius:11px;background:linear-gradient(180deg,#da2938,#8f0713);color:#fff;padding:11px 14px;font-weight:1000;cursor:pointer;text-decoration:none}.gacs-btn.secondary{background:rgba(255,255,255,.07);border-color:rgba(255,255,255,.16)}.gacs-btn:disabled{opacity:.5;cursor:wait}.gacs-status{display:flex;flex-wrap:wrap;gap:7px;margin:12px 0}.gacs-pill{padding:7px 9px;border-radius:999px;background:rgba(255,255,255,.08);font-size:10px;font-weight:1000}.gacs-pill.good{background:rgba(25,145,78,.25);color:#9cffc5}.gacs-pill.warn{background:rgba(145,87,0,.27);color:#ffd88b}.gacs-note{padding:13px;border-radius:12px;background:rgba(255,255,255,.045);color:rgba(255,255,255,.72);line-height:1.6;font-size:12px}.gacs-success,.gacs-error{margin-top:14px;padding:12px 14px;border-radius:12px;font-weight:800}.gacs-success{background:rgba(0,150,75,.18);border:1px solid rgba(65,230,135,.27);color:#aaffca}.gacs-error{background:rgba(170,14,30,.2);border:1px solid rgba(255,80,95,.3);color:#ffb8bf}.gacs-steps{padding-left:20px;line-height:1.8;color:rgba(255,255,255,.76)}.gacs-watch{word-break:break-all;color:#8fdcff;font-weight:900}.gacs-row{display:grid;grid-template-columns:minmax(160px,1fr) minmax(120px,.6fr) minmax(150px,.8fr) minmax(180px,.8fr);gap:10px;align-items:center;padding:13px;border-radius:13px;background:rgba(255,255,255,.035);border:1px solid rgba(255,255,255,.09);margin-top:9px}.gacs-name{font-weight:1000}.gacs-small{font-size:11px;color:rgba(255,255,255,.5);margin-top:3px;word-break:break-word}@media(max-width:820px){.gacs-grid{grid-template-columns:1fr}.gacs-row{grid-template-columns:1fr}.gacs-actions>.gacs-btn{flex:1 1 150px}}@media(max-width:520px){.gacs-page{width:calc(100% - 16px)}.gacs-card{padding:15px}.gacs-actions{display:grid;grid-template-columns:1fr}.gacs-actions>.gacs-btn{width:100%}}
      `}</style>

      <section className="gacs-hero">
        <div className="gacs-kicker">GASTREAMING DIRECT • CREW STREAMING</div>
        <h1>Console → Your GA Channel</h1>
        <p>Use the same GAStreaming channel whether you go live with OBS or directly from PS5 / Xbox. Twitch is only the native console transport; viewers still watch, chat, earn GAS Points and use your GA channel.</p>
        <div className="gacs-flow"><span>PS5 / Xbox</span><b>→</b><span>Twitch native broadcast</span><b>→</b><span>Your GAStreaming channel</span></div>
      </section>

      {message ? <div className="gacs-success">{message}</div> : null}
      {error ? <div className="gacs-error">{error}</div> : null}

      {profile ? (
        <section className="gacs-grid">
          <article className="gacs-card">
            <div className="gacs-kicker">YOUR CHANNEL</div>
            <h2>{profile.channelName}</h2>
            <div className="gacs-status">
              <span className={`gacs-pill ${profile.enabled ? "good" : "warn"}`}>{profile.enabled ? "CONSOLE ACCESS ACTIVE" : "ACCESS PAUSED"}</span>
              <span className={`gacs-pill ${profile.directConfigured ? "good" : "warn"}`}>{profile.directConfigured ? "TWITCH SOURCE CONNECTED" : "CONNECT TWITCH SOURCE"}</span>
              <span className={`gacs-pill ${profile.muxReady ? "good" : "warn"}`}>{profile.muxReady ? "OBS / MUX READY" : "MUX SETUP PENDING"}</span>
            </div>
            <div className="gacs-note">Your normal channel does not change. OBS and console-direct are two ways to feed the same GAStreaming destination.</div>
            <div className="gacs-note" style={{ marginTop: 10 }}>
              <strong>GA website music:</strong> {profile.websiteMusicEnabled ? "ON for console-direct viewers." : "OFF."} This layer never replaces your OBS/Mux audio.
              <div className="gacs-actions" style={{ marginTop: 9 }}>
                <button
                  type="button"
                  className={`gacs-btn ${profile.websiteMusicEnabled ? "secondary" : ""}`}
                  disabled={busy === "music"}
                  onClick={toggleWebsiteMusic}
                >
                  {busy === "music"
                    ? "UPDATING…"
                    : profile.websiteMusicEnabled
                    ? "TURN WEBSITE MUSIC OFF"
                    : "TURN WEBSITE MUSIC ON"}
                </button>
              </div>
            </div>
            <form className="gacs-form" onSubmit={saveSource} style={{ marginTop: 14 }}>
              <label className="gacs-label">Twitch username linked to your console
                <input className="gacs-input" value={form.twitchChannel} onChange={(e) => setForm((current) => ({ ...current, twitchChannel: e.target.value }))} placeholder="Example: nokap_slim" autoCapitalize="none" autoCorrect="off" />
              </label>
              <label className="gacs-label">Console you are broadcasting from
                <select className="gacs-input" value={form.platform} onChange={(e) => setForm((current) => ({ ...current, platform: e.target.value }))}>
                  <option value="ps5">PlayStation 5</option>
                  <option value="xbox">Xbox Series X|S</option>
                </select>
              </label>
              <div className="gacs-actions">
                <button className="gacs-btn" disabled={busy === "save" || !form.twitchChannel.trim()}>{busy === "save" ? "SAVING…" : "CONNECT CONSOLE SOURCE"}</button>
                {profile.directConfigured ? <button type="button" className="gacs-btn secondary" disabled={busy === "clear"} onClick={clearSource}>DISCONNECT DIRECT SOURCE</button> : null}
              </div>
            </form>
            <p><a className="gacs-watch" href={`/stream/${profile.channelSlug}`}>www.gastreaming.live/stream/{profile.channelSlug}</a></p>
          </article>

          <article className="gacs-card">
            <div className="gacs-kicker">HOW TO GO LIVE</div>
            <h2>Pick OBS or console</h2>
            <ol className="gacs-steps">
              <li>For OBS: keep using your normal GAStreaming/Mux stream setup. Nothing changes.</li>
              <li>For PS5: link Twitch on the console, then Create → Broadcast → Twitch.</li>
              <li>For Xbox: Guide → Capture & share → Live streaming → Twitch.</li>
              <li>Use the same Twitch username you connected on this page.</li>
              <li>Start the console broadcast. Open your normal GA channel — GA detects the console source and shows it with GA chat.</li>
              <li>When the console broadcast ends, the channel falls back to the existing GA Mux/VOD experience.</li>
            </ol>
            <div className="gacs-note"><strong>You do not need to give GAStreaming your Twitch stream key for this setup.</strong> Only the public Twitch username used by your console is saved.</div>
          </article>
        </section>
      ) : null}

      {crew ? (
        <section className="gacs-card">
          <div className="gacs-kicker">FOUNDER • CONSOLE CREW</div>
          <h2>Approved direct-console streamers</h2>
          <p>This roster is separate from tournament participation. These are permanent regular GAStreaming channels with both OBS/Mux and console-direct options.</p>
          <div className="gacs-actions"><button className="gacs-btn" onClick={reconcile} disabled={busy === "reconcile"}>{busy === "reconcile" ? "RECONCILING…" : "RECONCILE CREW + RETRY PENDING NOTIFICATIONS"}</button></div>
          {crew.map((entry) => (
            <div className="gacs-row" key={entry.id}>
              <div><div className="gacs-name">{entry.channelName}</div><div className="gacs-small">@{entry.username} • /stream/{entry.channelSlug}</div></div>
              <div><span className={`gacs-pill ${entry.enabled ? "good" : "warn"}`}>{entry.enabled ? "ACTIVE" : "PAUSED"}</span><div className="gacs-small">PS5 + Xbox</div></div>
              <div><span className={`gacs-pill ${entry.directConfigured ? "good" : "warn"}`}>{entry.directConfigured ? `@${entry.twitchChannel}` : "TWITCH NOT LINKED"}</span><div className="gacs-small">{entry.platform === "xbox" ? "Xbox Direct" : "PS5 Direct"}</div></div>
              <div>
                <div className="gacs-small">Bell/push: {statusDate(entry.announcement?.siteAt)}</div>
                <div className="gacs-small">Email: {statusDate(entry.announcement?.emailAt)}</div>
                <div className="gacs-actions" style={{ marginTop: 7 }}>
                  <button className="gacs-btn secondary" disabled={Boolean(busy)} onClick={() => adminAction(entry, "toggle")}>{entry.enabled ? "PAUSE" : "ENABLE"}</button>
                  {!entry.muxReady ? <button className="gacs-btn secondary" disabled={Boolean(busy)} onClick={() => adminAction(entry, "mux")}>RETRY MUX</button> : null}
                </div>
              </div>
            </div>
          ))}
        </section>
      ) : null}

      <Link to={crew ? "/admin" : "/streams"} style={{ display: "inline-block", marginTop: 18, color: "#fff", fontWeight: 900 }}>← Back to {crew ? "Founder Command" : "Channels"}</Link>
    </main>
  );
}
